import { useState } from 'react';
import { toast } from 'sonner';
import { InfoIcon } from 'lucide-react';
import { useGetCurrentWorkspaceId } from '@tinct/core-workspace';
import {
  useCheckLandingPageDomain,
  useDeleteLandingPageDomain,
  useGetLandingPageDomain,
  useUpdateLandingPageDomain,
} from '@tinct/core-api';
import { Alert, AlertDescription, Card, Skeleton } from '@tinct/web-components';
import { LayoutContent, LayoutHeader, LayoutTopbar } from '@tinct/web-layout';
import { CustomCodesPanel } from '../../components/custom-codes';
import { RegisterDomainCard } from './components/register-domain-card';
import { DomainProvisioningCard } from './components/domain-provisioning-card';
import { ActiveDomainCard } from './components/active-domain-card';
import { PublishedPagesCard } from './components/published-pages-card';
import { RemoveDomainModal } from './modals/remove-domain-modal';
import { ReplaceDomainModal } from './modals/replace-domain-modal';

// The server poller advances provisioning every ~2 min on its own; this only
// keeps the screen fresh while it's open.
const POLL_INTERVAL_MS = 30_000;

export function EditDomainHostingPage() {
  const workspaceId = useGetCurrentWorkspaceId();

  const [removeModalOpen, setRemoveModalOpen] = useState(false);
  const [replaceModalOpen, setReplaceModalOpen] = useState(false);

  const { data: domainConfig, isLoading } = useGetLandingPageDomain(
    { organizationId: workspaceId ?? '' },
    {
      enabled: !!workspaceId,
      refetchInterval: (query) => {
        const status = query.state.data?.status;
        return status === 'pending_dns' ||
          status === 'pending_validation' ||
          status === 'issued'
          ? POLL_INTERVAL_MS
          : false;
      },
      refetchIntervalInBackground: false,
    },
  );

  const updateMutation = useUpdateLandingPageDomain({
    onSuccess: () => {
      setReplaceModalOpen(false);
      toast.success('Domain saved — provisioning started');
    },
  });

  const checkMutation = useCheckLandingPageDomain({
    onError: (error) => toast.error(error.error),
  });

  const deleteMutation = useDeleteLandingPageDomain({
    onSuccess: () => {
      setRemoveModalOpen(false);
      toast.success('Domain removed — pages stay live on their tinct URL');
    },
    onError: (error) => toast.error(error.error),
  });

  const handleSaveDomain = (domain: string) => {
    if (!workspaceId) return;
    updateMutation.mutate({ organizationId: workspaceId, data: { domain } });
  };

  const handleRetry = () => {
    // Re-PUT of the same domain after `failed` restarts provisioning.
    if (!workspaceId || !domainConfig?.domain) return;
    updateMutation.mutate(
      { organizationId: workspaceId, data: { domain: domainConfig.domain } },
      // No form to carry the inline error here — surface it as a toast.
      { onError: (error) => toast.error(error.error) },
    );
  };

  const handleCheckNow = () => {
    if (!workspaceId) return;
    checkMutation.mutate({ organizationId: workspaceId });
  };

  const handleRemove = () => {
    if (!workspaceId) return;
    deleteMutation.mutate({ organizationId: workspaceId });
  };

  const domain = domainConfig?.domain ?? null;
  const status = domainConfig?.status ?? null;
  const serverError = updateMutation.error?.error ?? null;

  return (
    <div>
      <header className="sticky top-0 z-30">
        <LayoutTopbar />
      </header>
      <LayoutContent>
        <LayoutHeader
          pageTitle="Domain & hosting"
          pageDescription="Serve every published landing page under your own domain"
        />

        <div className="flex max-w-4xl flex-col gap-8">
          {isLoading && <Skeleton className="h-64 w-full rounded-xl" />}

          {/* 1. Empty state — no domain registered */}
          {!isLoading && !domain && (
            <RegisterDomainCard
              errorMessage={serverError}
              isSaving={updateMutation.isPending}
              onSave={handleSaveDomain}
            />
          )}

          {/* 2-3, 5. Provisioning & failed */}
          {!isLoading && domain && status && status !== 'active' && (
            <DomainProvisioningCard
              domain={domain}
              status={status}
              errorMessage={domainConfig?.error}
              dnsRecords={domainConfig?.requiredDnsRecords ?? []}
              isChecking={checkMutation.isPending}
              isRetrying={updateMutation.isPending}
              onCheckNow={handleCheckNow}
              onRetry={handleRetry}
              onRemove={() => setRemoveModalOpen(true)}
            />
          )}

          {/* 4. Active */}
          {!isLoading && domain && status === 'active' && (
            <>
              <ActiveDomainCard
                domain={domain}
                baseUrl={domainConfig?.baseUrl ?? `https://${domain}/`}
                onReplace={() => setReplaceModalOpen(true)}
                onRemove={() => setRemoveModalOpen(true)}
              />
              <PublishedPagesCard organizationId={workspaceId ?? ''} />
            </>
          )}

          <Alert variant="important">
            <InfoIcon className="size-4" />
            <AlertDescription>
              Your landing pages are always live on their tinct URL, with or
              without a custom domain. A custom domain adds a second address
              for every published page — one CNAME record covers the whole
              workspace, with no per-page setup.
            </AlertDescription>
          </Alert>

          {/* Workspace-wide custom code */}
          {workspaceId && (
            <Card className="gap-0 py-0">
              <div className="flex flex-col gap-1.5 px-6 pt-6">
                <h3 className="font-heading text-xl font-semibold leading-7 tracking-[-0.3px] text-foreground">
                  Custom code
                </h3>
                <p className="text-sm text-muted-foreground">
                  Scripts, styles and structured data loaded on every hosted
                  landing page of this workspace (GTM, PostHog, SEO…).
                </p>
              </div>
              <div className="px-6 py-6">
                <CustomCodesPanel scope={{ organizationId: workspaceId }} />
              </div>
            </Card>
          )}
        </div>
      </LayoutContent>

      {domain && (
        <RemoveDomainModal
          open={removeModalOpen}
          onOpenChange={setRemoveModalOpen}
          domain={domain}
          isRemoving={deleteMutation.isPending}
          onConfirm={handleRemove}
        />
      )}
      {domain && (
        <ReplaceDomainModal
          open={replaceModalOpen}
          onOpenChange={setReplaceModalOpen}
          currentDomain={domain}
          errorMessage={replaceModalOpen ? serverError : null}
          isReplacing={updateMutation.isPending}
          onConfirm={handleSaveDomain}
        />
      )}
    </div>
  );
}
